<template>
  <div :class="[componentClass.root, classes]">
    <div :class="componentClass.element('iconContainer')">
      <PmButtonPure
        :icon="icon"
        alternative="ghost"
        :variant="buttonVariant"
        :icon-hover="iconHover"
        :title="titleToggleUnread"
        @click="toggleUnread"
      />
    </div>

    <div :class="componentClass.element('container')">
      <div :class="componentClass.element('header')">
        <div :class="componentClass.element('headerContent')">
          <div :class="componentClass.element('date')">
            {{ formatToRelativeDate(date) }}
          </div>

          <div :class="componentClass.element('title')">{{ title }}</div>
        </div>

        <div :class="componentClass.element('actions')">
          <slot name="actions" />
        </div>
      </div>

      <div
        v-show="hasSlotContent.content"
        :class="componentClass.element('content')"
      >
        <slot name="content" />
      </div>

      <PmMetaListPure
        v-show="hasSlotContent.meta"
        :class="componentClass.element('meta')"
      >
        <slot name="meta" />
      </PmMetaListPure>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { formatToRelativeDate } from '@/utilities/date'

import PmButtonPure, {
  type Props as ButtonPureProps,
} from '@/components/basics/PmButtonPure.vue'
import type { Props as IconPureProps } from '@/components/basics/PmIcon/PmIconPure.vue'
import PmMetaListPure from '@/components/PmMetaList/PmMetaListPure.vue'
import { useHasSlotContent } from '@/composition/useHasSlotContent'
import { useComponentClass } from '@thomasaull-shared/composables'

export interface Props {
  notificationType:
    | 'resourceRequestFeedback'
    | 'leaveRequest'
    | 'externalServiceRequest'
    | 'error'
  date: Date
  isUnread?: boolean
  title?: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'markAsRead'): void
  (event: 'markAsUnread'): void
}>()

const hasSlotContent = useHasSlotContent(['content', 'meta'])
const componentClass = useComponentClass({ props })

const icon = computed<IconPureProps['name']>(() => {
  if (props.notificationType === 'resourceRequestFeedback')
    return 'mailQuestion'

  if (props.notificationType === 'leaveRequest') return 'vacation'
  if (props.notificationType === 'externalServiceRequest') return 'alien'
  if (props.notificationType === 'error') return 'alert'

  return 'info'
})

const buttonVariant = computed<ButtonPureProps['variant'] | undefined>(() => {
  if (props.notificationType === 'error') return 'danger'
  return undefined
})

const iconHover = computed<IconPureProps['name']>(() => {
  return props.isUnread ? 'check' : 'markAsUnread'
})

const titleToggleUnread = computed(() => {
  if (props.isUnread) return 'Als gelesen markieren'
  return 'Als ungelesen markieren'
})

const classes = computed(() => {
  return [
    componentClass.propModifier({ name: 'notificationType' }),
    {
      'is-unread': props.isUnread,
    },
  ]
})

function toggleUnread() {
  if (props.isUnread) emit('markAsRead')
  else emit('markAsUnread')
}
</script>

<style lang="scss">
@use 'sass:math';

.PmNotificationContainerPure {
  $block: &;

  display: flex;
  gap: 12px;

  &--notificationTypeError {
    background-color: color.$danger-50;
    border: 1px solid color.$danger-500;
    border-radius: 4px;
    padding: 8px;
  }

  &-iconContainer {
    position: relative;
    align-self: flex-start;
  }

  &-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &-header {
    display: flex;
  }

  &-headerContent {
    margin-right: auto;
  }

  &-title {
    font-weight: 600;
    line-height: 1.3;

    #{$block}--notificationTypeError & {
      color: color.$danger-700;
    }
  }

  &-date {
    @include mixin.textLabel;

    margin-bottom: 4px;
    position: relative;
    display: flex;
    gap: 0.5em;
    align-items: center;

    #{$block}.is-unread & {
      color: color.$danger-600;
    }

    #{$block}--notificationTypeError & {
      color: color.$danger-700;
    }

    &::before {
      $size: 8px;

      content: '';
      background-color: color.$danger-500;
      width: $size;
      height: $size;
      border-radius: $size;
      display: block;
      position: relative;
      top: 1px;

      #{$block}:not(.is-unread) & {
        display: none;
      }
    }
  }

  &-actions {
    margin-left: 16px;
    flex-shrink: 0;
  }

  &-content {
    min-width: 0;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    #{$block}--notificationTypeError & {
      color: color.$danger-700;
    }
  }

  &-meta {
    width: 90%;
    margin-top: 8px;
  }
}
</style>
